/**
 * ChatComposer — ô soạn tin của cả hai phía (khách ở cổng, nhân viên ở
 * /customer-chats). Tách khỏi `ChatThread.tsx` để mỗi tệp một việc: khung đọc
 * hội thoại một bên, ô gõ một bên.
 */

import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, SendHorizontal, X } from "lucide-react";
import { imagesFromClipboard } from "@/lib/clipboard-images";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import type { ChatMessage } from "./api";
import { StagedImagesStrip, useStagedImages } from "./composer/useStagedImages";
import { EmojiPicker } from "./EmojiPicker";
import { MAX_BODY_CHARS, useEmojiInsert } from "./useEmojiInsert";

/**
 * Ô soạn tin (CEO chốt 25/09/2026 — "2a", làm giống ghi chú đơn hàng):
 *
 * - Ảnh vào HÀNG CHỜ chứ không bắn đi ngay lúc chọn. Trước đây chọn 3 ảnh là
 *   3 tin nhắn rời bay đi trước cả khi người gửi kịp gõ chữ.
 * - Ảnh ĐẦU TIÊN mang luôn phần chữ đang gõ ⇒ ảnh và lời nhắn về khách thành
 *   MỘT tin. Máy chủ đã nhận `body` kèm tệp từ trước (`sendAdminChatImage` /
 *   `sendCustomerChatImage` / `sendPartnerChatImage`), đây chỉ là nối dây.
 *   Các ảnh sau đi không chữ — gắn lại cùng một lời nhắn lên từng ảnh là
 *   nhân bản chữ trong khung chat của khách.
 * - Dán ảnh bằng Ctrl+V vào ô soạn, ảnh rơi vào cùng hàng chờ đó.
 */
export function ChatComposer({
  onSendText,
  onSendImage,
  sending,
  replyTo,
  onCancelReply,
}: {
  onSendText: (body: string) => Promise<unknown>;
  onSendImage: (file: File, caption?: string) => Promise<unknown>;
  sending: boolean;
  replyTo?: ChatMessage | null | undefined;
  onCancelReply?: (() => void) | undefined;
}) {
  const [text, setText] = useState("");
  const { images, add: themAnh, remove: boAnh, clear: xoaHetAnh } = useStagedImages();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const { textRef, insertEmoji } = useEmojiInsert(text, setText);

  const submit = async () => {
    const body = text.trim();
    if ((!body && images.length === 0) || sending) return;
    try {
      if (images.length === 0) {
        await onSendText(body);
      } else {
        // TUẦN TỰ, không Promise.all: máy chủ chốt thứ tự theo created_at, bắn
        // song song là ảnh về lộn xộn so với lúc chọn.
        for (const [i, img] of images.entries()) {
          await onSendImage(img.file, i === 0 && body ? body : undefined);
        }
        xoaHetAnh();
      }
      setText("");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Không gửi được tin nhắn");
    }
  };

  return (
    <div className="border-t bg-surface">
      {replyTo && (
        <div className="flex items-start gap-2 border-b bg-surface-2 px-3 py-2 text-xs">
          <div className="min-w-0 flex-1 border-l-2 border-[var(--color-brand)] pl-2">
            <span className="block font-medium">
              {replyTo.senderType === "customer" ? "Khách" : (replyTo.sender?.name ?? "Gạo Nâu")}
            </span>
            <span className="block truncate text-muted-foreground">
              {replyTo.body || "[Hình ảnh]"}
            </span>
          </div>
          <Button type="button" variant="ghost" size="icon" aria-label="Bỏ trích dẫn" onClick={onCancelReply}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      {images.length > 0 && <StagedImagesStrip images={images} onRemove={boAnh} disabled={sending} />}
      <form
        className="flex items-center gap-2 p-2"
        onPaste={(event) => {
          const files = imagesFromClipboard(event.clipboardData);
          // Không có ảnh thì để yên cho lượt dán CHỮ chạy như cũ.
          if (files.length === 0) return;
          event.preventDefault();
          themAnh(files);
        }}
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          aria-label="Chọn ảnh gửi khách"
          onChange={(event) => {
            themAnh(Array.from(event.target.files ?? []));
            if (fileRef.current) fileRef.current.value = "";
          }}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Gửi ảnh"
          disabled={sending}
          onClick={() => fileRef.current?.click()}
        >
          <ImagePlus className="h-5 w-5" />
        </Button>
        <EmojiPicker onPick={insertEmoji} disabled={sending} />
        <Input
          ref={textRef}
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={images.length > 0 ? "Thêm lời nhắn cho ảnh…" : "Nhập tin nhắn…"}
          className="min-h-11 flex-1"
          maxLength={MAX_BODY_CHARS}
        />
        <Button
          type="submit"
          size="icon"
          aria-label="Gửi"
          disabled={sending || (!text.trim() && images.length === 0)}
        >
          <SendHorizontal className="h-5 w-5" />
        </Button>
      </form>
    </div>
  );
}
